/**
 * LNL Theme system — CSS-variable + Google Fonts loader.
 * Centralizes brand colors, presets, and font families for the whole site.
 */

// Font catalog exposed in the CMS Font picker. Google Fonts names — must match spelling.
export const FONT_CATALOG = [
    { key: "Plus Jakarta Sans", weights: "400;500;600;700;800" },
    { key: "Manrope", weights: "400;500;600;700;800" },
    { key: "Inter", weights: "400;500;600;700" },
    { key: "Roboto", weights: "400;500;700" },
    { key: "Open Sans", weights: "400;600;700" },
    { key: "Poppins", weights: "400;500;600;700" },
    { key: "Montserrat", weights: "400;500;600;700;800" },
    { key: "Lato", weights: "400;700;900" },
    { key: "Nunito", weights: "400;600;700;800" },
    { key: "Source Sans 3", weights: "400;600;700" },
    { key: "DM Sans", weights: "400;500;700" },
    { key: "Merriweather", weights: "400;700" },
    { key: "Playfair Display", weights: "400;600;700;800" },
];

// Editable token keys (each maps to a CSS variable on :root)
export const COLOR_TOKENS = [
    { key: "primary",        label: "Brand primary",       cssVar: "--lnl-green",         group: "Brand" },
    { key: "primary_deep",   label: "Brand primary (deep)",cssVar: "--lnl-green-deep",    group: "Brand" },
    { key: "gold",           label: "Accent gold",         cssVar: "--lnl-gold",          group: "Brand" },
    { key: "gold_soft",      label: "Accent gold (soft)",  cssVar: "--lnl-gold-soft",     group: "Brand" },
    { key: "ink",            label: "Institutional ink",   cssVar: "--lnl-ink",           group: "Brand" },
    { key: "bg",             label: "Page background",     cssVar: "--lnl-white",         group: "Surface" },
    { key: "off_white",      label: "Off-white surface",   cssVar: "--lnl-off-white",     group: "Surface" },
    { key: "green_bg",       label: "Soft green tint",     cssVar: "--lnl-green-bg",      group: "Surface" },
    { key: "border",         label: "Border",              cssVar: "--lnl-border",        group: "Surface" },
    { key: "charcoal",       label: "Primary text",        cssVar: "--lnl-charcoal",      group: "Text" },
    { key: "gray",           label: "Muted text",          cssVar: "--lnl-gray",          group: "Text" },
];

// Preset themes — each preset provides full color set + fonts.
export const THEME_PRESETS = {
    lnl_default: {
        label: "LNL Default",
        fonts: { heading: "Plus Jakarta Sans", body: "Inter" },
        colors: {
            primary: "#006800", primary_deep: "#005600", gold: "#d4af37", gold_soft: "#e8c86a",
            ink: "#0F1A11", bg: "#ffffff", off_white: "#f7f8f3", green_bg: "#eef6ea",
            border: "#dde5dc", charcoal: "#1b1f1c", gray: "#5e665f",
        },
    },
    academic_blue: {
        label: "Academic Blue",
        fonts: { heading: "Merriweather", body: "Source Sans 3" },
        colors: {
            primary: "#0f3d78", primary_deep: "#0a2a52", gold: "#c9a94c", gold_soft: "#e4cf85",
            ink: "#0b1424", bg: "#ffffff", off_white: "#f3f6fb", green_bg: "#e6ecf7",
            border: "#d5dde8", charcoal: "#1a2338", gray: "#5a6478",
        },
    },
    professional: {
        label: "Professional",
        fonts: { heading: "Montserrat", body: "Inter" },
        colors: {
            primary: "#1f2937", primary_deep: "#111827", gold: "#b7791f", gold_soft: "#d69e2e",
            ink: "#0b1220", bg: "#ffffff", off_white: "#f6f7f8", green_bg: "#eef1f4",
            border: "#dde2e8", charcoal: "#1f2937", gray: "#4b5563",
        },
    },
    modern: {
        label: "Modern",
        fonts: { heading: "Poppins", body: "Nunito" },
        colors: {
            primary: "#0f766e", primary_deep: "#115e59", gold: "#f97316", gold_soft: "#fdba74",
            ink: "#0b3a34", bg: "#ffffff", off_white: "#f5faf9", green_bg: "#e6f5f2",
            border: "#d4e6e2", charcoal: "#0f2b28", gray: "#4b6360",
        },
    },
    elegant: {
        label: "Elegant",
        fonts: { heading: "Playfair Display", body: "Lato" },
        colors: {
            primary: "#2f4f2f", primary_deep: "#233a23", gold: "#c19a3d", gold_soft: "#d9b967",
            ink: "#231e1a", bg: "#fbf9f5", off_white: "#f4efe5", green_bg: "#eaf1e6",
            border: "#e2dccc", charcoal: "#3a2f24", gray: "#6b6255",
        },
    },
    high_contrast: {
        label: "High Contrast",
        fonts: { heading: "Manrope", body: "Inter" },
        colors: {
            primary: "#000000", primary_deep: "#000000", gold: "#ffd60a", gold_soft: "#ffe566",
            ink: "#000000", bg: "#ffffff", off_white: "#f5f5f5", green_bg: "#f2f2f2",
            border: "#000000", charcoal: "#000000", gray: "#333333",
        },
    },
};

const LOADED_FONTS = new Set();

export function loadGoogleFont(family) {
    if (!family || LOADED_FONTS.has(family)) return;
    const font = FONT_CATALOG.find((f) => f.key === family);
    if (!font) return;
    const id = `gfont-${family.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) { LOADED_FONTS.add(family); return; }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.id = id;
    link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/\s+/g, "+")}:wght@${font.weights}&display=swap`;
    document.head.appendChild(link);
    LOADED_FONTS.add(family);
}

export function applyTheme(theme, { includeFonts = true } = {}) {
    if (!theme) return;
    const root = document.documentElement;
    const colors = theme.colors || {};
    COLOR_TOKENS.forEach(({ key, cssVar }) => {
        if (colors[key]) root.style.setProperty(cssVar, colors[key]);
    });
    if (includeFonts && theme.fonts) {
        const { heading, body } = theme.fonts;
        if (heading) loadGoogleFont(heading);
        if (body) loadGoogleFont(body);
        if (body) document.body.style.fontFamily = `"${body}", ui-sans-serif, system-ui, sans-serif`;
        if (heading) {
            let styleEl = document.getElementById("lnl-heading-font-style");
            if (!styleEl) {
                styleEl = document.createElement("style");
                styleEl.id = "lnl-heading-font-style";
                document.head.appendChild(styleEl);
            }
            styleEl.textContent = `
                h1,h2,h3,h4,h5,h6,.font-heading,.font-display{
                    font-family:"${heading}", ui-sans-serif, system-ui, sans-serif !important;
                }
            `;
        }
    }
}

// Reset previously-applied inline CSS variables and font style tag.
export function clearAppliedTheme() {
    const root = document.documentElement;
    COLOR_TOKENS.forEach(({ cssVar }) => root.style.removeProperty(cssVar));
    document.body.style.removeProperty("font-family");
    const styleEl = document.getElementById("lnl-heading-font-style");
    if (styleEl) styleEl.remove();
}

// WCAG relative luminance ratio between two hex colours.
export function contrastRatio(hex1, hex2) {
    const toLum = (h) => {
        const s = (h || "").replace("#", "");
        if (s.length < 6) return 0;
        const parts = s.match(/.{2}/g).map((x) => parseInt(x, 16) / 255);
        const [r, g, b] = parts.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    };
    const l1 = toLum(hex1);
    const l2 = toLum(hex2);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export function contrastLabel(ratio) {
    if (ratio >= 7) return { level: "AAA", tone: "ok", msg: "Excellent contrast" };
    if (ratio >= 4.5) return { level: "AA", tone: "ok", msg: "Good contrast" };
    if (ratio >= 3) return { level: "AA-large", tone: "warn", msg: "OK for large text only" };
    return { level: "Fail", tone: "bad", msg: "Poor contrast — text hard to read" };
}
